import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

// Custom hooks
import { useChatFlow } from '../hooks/useChatFlow';
import { useFileUpload } from '../hooks/useFileUpload';
import { useChatSubmit } from '../hooks/useChatSubmit';

// Components
import {
  ChatHeader,
  ChatMessageBubble,
  ChatTypingIndicator,
  ChatInputArea,
  ChatAuthForm,
  ChatCompanyLookup,
  ChatDragOverlay
} from '../components/chat';
import PackageSelector from '../components/afa/PackageSelector';
import AFAFileUpload from '../components/afa/AFAFileUpload';
import EscalationModal from '../components/afa/EscalationModal';

/**
 * ChatPage - Client intake chat interface
 * Uses custom hooks for flow, file upload, and submission logic
 */
const ChatPage = () => {
  const navigate = useNavigate();
  const user = auth.getUser();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Chat flow hook
  const {
    messages,
    isTyping,
    clientType,
    isExistingCustomer,
    showCompanyLookup,
    companyLookupInput,
    setCompanyLookupInput,
    caseCategory,
    caseSubCategory,
    showInput,
    setShowInput,
    caseSummary,
    isInReviewMode,
    setIsInReviewMode,
    addMessage,
    setIsTyping,
    handleQuickReply,
    handleCategorySelect,
    handleSubCategorySelect,
    triggerAIReview,
    handleCompanyLookup
  } = useChatFlow();

  // File upload hook
  const {
    uploadedFile,
    setUploadedFile,
    isDragging,
    attachments,
    setAttachments,
    handleFileSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop
  } = useFileUpload();

  // Submit hook
  const {
    selectedPackage,
    showPackageSelector,
    showFileUpload,
    setShowFileUpload,
    triageResult,
    showEscalationModal,
    setShowEscalationModal,
    showAuthForm,
    authMode,
    setAuthMode,
    authForm,
    setAuthForm,
    authError,
    setAuthError,
    isSubmittingAuth,
    handleReviewAction,
    handlePackageSelected,
    handleFileUploadContinue,
    handleContinueWithPackage,
    handleUpgradeToFullCase,
    handleAuthSubmit
  } = useChatSubmit({
    messages,
    clientType,
    caseCategory,
    caseSubCategory,
    caseSummary,
    isExistingCustomer,
    attachments,
    addMessage,
    setIsTyping,
    setShowInput,
    setIsInReviewMode
  });

  // Auto-logout lawyers/admins
  useEffect(() => {
    if (user && (user.role === 'LAWYER' || user.role === 'ADMIN' || user.role === 'TENANT_ADMIN')) {
      auth.logout();
      toast.success('Du er n\u00e5 logget ut fra Lexy. Du kan n\u00e5 starte din kunde-sak!');
      setTimeout(() => { window.location.href = '/chat'; }, 500);
    }
  }, [user]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Focus input when showInput changes
  useEffect(() => {
    if (showInput) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [showInput]);

  // Input state
  const [input, setInput] = React.useState('');

  // Handle send message
  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;

    const userMessage = input;
    addMessage('user', userMessage);
    if (uploadedFile) addMessage('user', `\ud83d\udcce ${uploadedFile.name}`);
    
    setInput('');
    setShowInput(false);
    await triggerAIReview(userMessage, uploadedFile);
  };

  // Calculate bottom padding based on visible UI elements
  const getBottomPadding = () => {
    if (showInput) return 'h-56';
    if (showAuthForm) return 'h-[500px]';
    if (showPackageSelector) return 'h-[700px]';
    if (showFileUpload) return 'h-[600px]';
    return 'h-20';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      <ChatHeader />

      {/* Messages */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {messages.map((msg: any, index: number) => (
            <ChatMessageBubble
              key={msg.id || index}
              message={msg}
              onQuickReply={handleQuickReply}
              onCategorySelect={handleCategorySelect}
              onSubCategorySelect={handleSubCategorySelect}
              onReviewAction={handleReviewAction}
            />
          ))}
          {isTyping && <ChatTypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Company Lookup Input */}
      {showCompanyLookup && (
        <ChatCompanyLookup
          value={companyLookupInput}
          onChange={setCompanyLookupInput}
          onSubmit={handleCompanyLookup}
        />
      )}

      {/* Main Chat Input */}
      {showInput && (
        <ChatInputArea
          input={input}
          setInput={setInput}
          onSubmit={handleSend}
          isTyping={isTyping}
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          caseSubCategory={caseSubCategory}
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onFileSelect={handleFileSelect}
        />
      )}

      {/* Package Selector */}
      {showPackageSelector && !selectedPackage && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent z-30 pt-20 pb-6">
          <div className="max-w-5xl mx-auto px-6">
            <PackageSelector onSelect={handlePackageSelected} />
          </div>
        </div>
      )}

      {/* File Upload */}
      {showFileUpload && selectedPackage && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent z-30 pt-20 pb-6">
          <div className="max-w-5xl mx-auto px-6">
            <AFAFileUpload 
              maxFiles={selectedPackage.docs} 
              onFilesChange={setAttachments} 
              onContinue={() => handleFileUploadContinue(attachments)} 
            />
          </div>
        </div>
      )}

      {/* Escalation Modal */}
      <EscalationModal
        isOpen={showEscalationModal}
        onClose={() => setShowEscalationModal(false)}
        triageResult={triageResult}
        selectedPackage={selectedPackage}
        onContinueWithPackage={handleContinueWithPackage}
        onUpgradeToFullCase={handleUpgradeToFullCase}
      />

      {/* Auth Form */}
      {showAuthForm && (
        <ChatAuthForm
          authMode={authMode}
          setAuthMode={setAuthMode}
          authForm={authForm}
          setAuthForm={setAuthForm}
          authError={authError}
          setAuthError={setAuthError}
          isSubmitting={isSubmittingAuth}
          onSubmit={handleAuthSubmit}
        />
      )}

      {/* Drag Overlay */}
      {isDragging && <ChatDragOverlay />}

      {/* Bottom Padding */}
      <div className={getBottomPadding()}></div>
    </div>
  );
};

export default ChatPage;
